import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import * as io from 'socket.io-client';

import { ToastrService, IndividualToastrConfig, ActiveToast } from 'ngx-toastr';
import { AppinfoService } from './appinfo.service';
import { UserService } from './user.service';
import { AppSettings } from './../app.settings';

// Socket message passed from backend
export interface SocketMessage {
	processId: string;
	processType: 'Upload';
	isFinal: boolean;
	isRealTime: boolean; // TODO
	message: {
		type: 'print' | 'percent' | 'json';
		content: any;
	};
	timeStamp: Date;
	// These will only exist in final message
	isError?: boolean;
	beginTime?: Date;
	endTime?: Date | string;
	cancelled?: boolean;
}
// Socket message interface for frontend use
export interface SocketMessageFE {
	processId: string;
	processType: ProcessType;
	isFinal: boolean;
	isError: boolean;
	isCanceled: boolean;
	dataType: 'print' | 'percent' | 'json';
	data: any;
	timeStamp: Date | string;
	info?: {[prop: string]: any};
}
export type ProcessType = 'Upload';

export interface InitProcess {
	processType: ProcessType;
	tempId: string;
	processId?: string;
	status?: string;
	info?: {[prop: string]: any};
}

// Socket logs by processId. This is for use of displaying logs in common-socket-logs
export interface SocketLogs {
	[processId: string]: SocketLog;
}
export interface SocketLog {
	socketMsg: SocketMessageFE[];
	toastId?: number;
	isCurrent?: boolean; // Is current message to display in the logs page
	isFinished?: boolean;
	isError?: boolean;
	percent?: number;
	info?: {[prop: string]: any};
}

@Injectable()
export class SocketService {

	private socket: SocketIOClient.Socket;
	public toastrConfig: Partial<IndividualToastrConfig> = {
		enableHtml: true
	};

	// Store whether a process's logs has been refreshed or not after socket re-connecting
		// This is for case:
		// socket disconnects then connects, all the logs will be sent one more time. The old logs need to be cleaned or it will repeat.
	public refreshedProcessesById: { [processId: string]: boolean };

	// On initing / preparing a process
	public onInitProcess: Subject<InitProcess> = new Subject();
	//
	public messageSubject: Subject<SocketMessageFE> = new Subject();

	constructor(
		private toastr: ToastrService,
		private appinfoService: AppinfoService,
		private userService: UserService
	) { }

	getSocketId() {
		return this.socket.id;
	}
	connectSocket() {
		console.log('connect socket...');
		if (this.socket && this.socket.disconnected) {
			this.socket.connect();
		} else if (!this.socket) {
			try {
				this.socket = io(AppSettings.serviceRootUrl);
			} catch (err) {
				console.log(err);
			}

			this.socket.on('disconnect', (data) => {
				console.log('dddddddddddddddddddisconnect');
				console.log(data);
			});
			this.socket.on('error', (err) => {
				console.log(err);
			});
			this.socket.on('connect', () => {
				console.log('connect event: ', this.socket.id);
				// Init refreshedProcessesById
				this.refreshedProcessesById = {};
				// Emit init info
				this.socket.emit('init', {
					userSubscriptionId: this.userService.getUserInfo().userSubId,
					sessionId: this.appinfoService.getSessionUuid()
				});
			});
			this.socket.on('message', (data) => {
				console.log(data);
				const formattedData = this.handleData(data);
				switch (formattedData.processType) {
					case 'Upload':
						this.messageSubject.next(formattedData);
						break;
				}
			});

		}
	}

	disconnectSocket() {
		this.socket.disconnect();
	}
	// Generate toast message
	public generateToastMessage(type: 'processing' | 'final', msg?: string, titleMsg?: string): string {
		msg = msg || '';
		switch (type) {
			case 'processing':
				titleMsg = titleMsg || 'Uploading';
				return `<div>${titleMsg} <div class="loading-dots"></div></div>
				<div>${msg}</div>`;
			case 'final':
				// Set Default msg
				msg = msg || `<div>Successfully uploaded</div>`;
				return msg;
		}
	}
	// Generate toast
	public generateToast(type: 'info' | 'success' |'error' | 'warning', message: string, onTapCallback? ): ActiveToast {
		let toast: ActiveToast;
		const config: Partial<IndividualToastrConfig> = Object.assign({}, this.toastrConfig);
		config.timeOut = 10000;
		config.extendedTimeOut = 10000;
		switch (type) {
			case 'info':
				toast = this.toastr.info(message, null, config);
				break;
			case 'success':
				config.tapToDismiss = true;
				toast = this.toastr.success(message, null, config);
				break;
			case 'error':
				toast = this.toastr.error(message, null, this.toastrConfig);
				break;
			case 'warning':
				toast = this.toastr.warning(message, null, this.toastrConfig);
		}
		if (onTapCallback) {
			toast.onTap.subscribe(() => {
				onTapCallback();
			});
		}
		return toast;
	}
	// Process with socket info
	registerProcessById(processId: string, processesById): SocketLog {
		// Set already existed process isCurrent as false
		for (const id of Object.keys(processesById)) {
			processesById[id].isCurrent = false;
		}
		// Register the new process
		const process = {
			toastId: null,
			socketMsg: [],
			isCurrent: true // Make the latest received process the current one to display in the logs page
		};
		processesById[processId] = process;
		return process;
	}
	// Get current socket log from SocketLogs
	getCurrentSocketLog(processId: string, socketLogs: SocketLogs): SocketLog {
		if (!processId) {
			for (const id of Object.keys(socketLogs)) {
				if (socketLogs[id].isCurrent) {
					processId = id;
					break;
				}
			}
		}
		if (socketLogs[processId]) {
			return socketLogs[processId];
		}
		return null;
	}
	// Remove toast
	removeToast(toastId) {
		if (toastId || toastId === 0) {
			this.toastr.remove(toastId);
		}
	}

	public onSocketUpdate(
		data: SocketMessageFE,
		logsBehaviorSubject: BehaviorSubject<{[id: string]: SocketLog}>,
		messages: {success: string, canceled: string},
		onFinal?
	) {
		// All socket logs
		const processesById = logsBehaviorSubject.getValue();
		// Socket logs of the current process
		const process = processesById[data.processId] || this.registerProcessById(data.processId, processesById);
		// Clear old socket logs if socket reconnects
		if (this.refreshedProcessesById && !this.refreshedProcessesById[data.processId]) {
			process.socketMsg.length = 0;
			// Do not use process.socketMsg = [] to clear messages, since the object reference will change and causing no-display issue
			// process.socketMsg = [];
			this.refreshedProcessesById[data.processId] = true;
		}
		// Push socket message to process.socketMsg
		process.socketMsg.push(data);
		// Current toastId
		const toastId = process.toastId;
		this.removeToast(toastId);
		// Finished
		if (data.isFinal) {
			process.isFinished = true;
			if (data.isError) { // Error
				process.isError = true;
				// Toast error
				const err = data.data;
				// this.removeToast(toastId);
				if (err && err.errorMessage) {
					process.toastId = this.generateToast('error', this.generateToastMessage('final', err.errorMessage + '' + err.errorDetail)).toastId;
					// this.generateToast('success', this.generateToastMessage('final', message.content));
				}
			} else if (data.isCanceled) { // Canceled
				process.toastId = this.generateToast('error', this.generateToastMessage('final', messages.canceled)).toastId;
			} else { // Completed
				// this.removeToast(toastId);
					process.toastId = this.generateToast('success', this.generateToastMessage('final', messages.success)).toastId;
					// this.generateToast('success', this.generateToastMessage('final', message.content));
					// process.socketMsg
			}
			if (onFinal) {
				onFinal();
			}
		}
		// Not Fnished
		else if (data.dataType === 'print') {
			// if (!process.completed) {
			// 	this.removeToast(toastId);
			// 	const toast = this.generateToast('info', this.generateToastMessage('processing', data.data, 'Parsing'));
			// 	process.toastId = toast.toastId;
			// }
		} else if (data.dataType === 'percent') {
			// TODO
			// process.percent = Math.floor(data.data.batchNumber / data.data.totalBatch * 100);
		}
		logsBehaviorSubject.next(processesById);
	}

	private handleData(data: string): SocketMessageFE {
		const dataObj: SocketMessage = JSON.parse(data);
		console.log(dataObj);
		const formattedData: SocketMessageFE = {
			processId: dataObj.processId,
			processType: dataObj.processType,
			isFinal: dataObj.isFinal,
			isError: dataObj.isError,
			isCanceled: dataObj.cancelled,
			dataType: dataObj.message.type,
			data: dataObj.message.content,
			timeStamp: dataObj.timeStamp
		};
		return formattedData;
	}

}



// WEBPACK FOOTER //
// ./src/app/common-services/socket.service.ts